import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import TemplateCard from './TemplateCard';
import { 
  getCategoriesWithTemplates, 
  getTemplateById,
  TEMPLATE_CATEGORIES 
} from '../../utils/llm-generator/promptTemplates';

/**
 * Template Selector Component
 * Allows users to select from different subject area templates
 * Organized by categories (MINT, Languages, Humanities, Arts)
 */
function TemplateSelector({ 
  selectedTemplateId, 
  onTemplateSelect,
  showSystemPromptToggle = true,
  onSystemPromptToggle,
  isSystemPromptVisible = false,
  compact = false
}) {
  const [activeCategory, setActiveCategory] = useState('mint');
  
  // Get categories with their templates
  const categoriesWithTemplates = useMemo(() => getCategoriesWithTemplates(), []);
  
  // Get templates for active category
  const activeTemplates = useMemo(() => {
    const category = categoriesWithTemplates.find(c => c.id === activeCategory);
    return category?.templates || [];
  }, [activeCategory, categoriesWithTemplates]);

  // Get selected template info
  const selectedTemplate = useMemo(() => 
    selectedTemplateId ? getTemplateById(selectedTemplateId) : null, 
    [selectedTemplateId]
  );

  const handleTemplateSelect = (template) => {
    onTemplateSelect(template.id);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">📋</span>
            <h3 className="font-medium text-gray-900 dark:text-white">
              Fachbereich auswählen
            </h3>
          </div>
          {selectedTemplate && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span>Aktiv:</span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                <span>{selectedTemplate.icon}</span>
                <span className="font-medium">{selectedTemplate.name}</span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30 overflow-x-auto">
        {Object.values(TEMPLATE_CATEGORIES).map((category) => (
          <button
            key={category.id}
            type="button"
            onClick={() => setActiveCategory(category.id)}
            className={`
              flex-shrink-0 px-4 py-3 text-sm font-medium theme-transition
              border-b-2 -mb-px
              ${activeCategory === category.id
                ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-800'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }
            `}
          >
            <span className="mr-2">{category.icon}</span>
            {category.name}
          </button>
        ))}
      </div>

      {/* Category Description */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/20 border-b border-gray-100 dark:border-gray-700/50">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {TEMPLATE_CATEGORIES[activeCategory]?.description}
        </p>
      </div>

      {/* Template Grid */}
      <div className={`p-4 ${compact ? 'max-h-64 overflow-y-auto' : ''}`}>
        <div 
          className={compact 
            ? 'grid grid-cols-1 gap-3' 
            : 'grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'
          }
          style={compact ? {} : { gridAutoRows: '1fr' }}
        >
          {activeTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              isSelected={selectedTemplateId === template.id}
              onClick={handleTemplateSelect}
              size={compact ? 'small' : 'medium'}
            />
          ))}
        </div>
        
        {activeTemplates.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <span className="text-2xl mb-2 block">🔍</span>
            <p>Keine Vorlagen in dieser Kategorie</p>
          </div>
        )}
      </div>

      {/* System Prompt Toggle */}
      {showSystemPromptToggle && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isSystemPromptVisible}
              onChange={(e) => onSystemPromptToggle?.(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              System Prompt anzeigen & bearbeiten
            </span>
          </label>
        </div>
      )}

      {/* Selected Template Info */}
      {selectedTemplate && !compact && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20">
          <div className="flex items-start gap-3">
            <span className="text-2xl">{selectedTemplate.icon}</span>
            <div className="flex-1">
              <h4 className="font-medium text-blue-900 dark:text-blue-100">
                {selectedTemplate.name}
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                {selectedTemplate.description}
              </p>
              {selectedTemplate.exampleTags && selectedTemplate.exampleTags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  <span className="text-xs text-blue-600 dark:text-blue-400 mr-1">Beispiel-Tags:</span>
                  {selectedTemplate.exampleTags.slice(0, 6).map((tag, index) => (
                    <span
                      key={index}
                      className="inline-block px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

TemplateSelector.propTypes = {
  selectedTemplateId: PropTypes.string,
  onTemplateSelect: PropTypes.func.isRequired,
  showSystemPromptToggle: PropTypes.bool,
  onSystemPromptToggle: PropTypes.func,
  isSystemPromptVisible: PropTypes.bool,
  compact: PropTypes.bool
};

TemplateSelector.defaultProps = {
  selectedTemplateId: null,
  showSystemPromptToggle: true,
  isSystemPromptVisible: false,
  compact: false
};

export default TemplateSelector;
